import * as d3 from "d3";
import { TrendCurveChartData } from "../../interface/DesignerInterface";
import {
  TREND_CURVE_CHART_HEIGHT,
  TREND_CURVE_EXPANDED_CHART_HEIGHT,
  TREND_CURVE_EXPANDED_CHART_WIDTH,
  TREND_CURVE_LEGEND_MAX_ITEMS,
  TREND_CURVE_SIDE_LEGEND_WIDTH_COMPACT,
  TREND_CURVE_SIDE_LEGEND_WIDTH_EXPANDED,
} from "../../constants/trendCurveConstants";
import {
  buildTrendCurveTooltipHtml,
  computeTrendCurveBottomMargin,
  computeTrendCurveContributionLabelLayout,
  computeTrendCurvePreferredChartWidth,
  computeTrendCurveXPadding,
  computeTrendCurveYAxisMax,
  formatTrendCurveContribution,
  getTrendCurveLegendItems,
  getTrendCurveXAxisTickLabels,
  shouldRotateTrendCurveXLabels,
  shouldShowTrendCurveContributionLabel,
  truncateTrendCurveLegendLabel,
} from "./trendCurveChartHelpers";
import { formatTrendHeaderChips } from "../designerPage-utils";

export {
  TREND_CURVE_CHART_HEIGHT,
  TREND_CURVE_EXPANDED_CHART_HEIGHT,
  TREND_CURVE_DISTINCT_COLOR_COUNT,
  TREND_CURVE_EXPANDED_CHART_WIDTH,
  TREND_CURVE_LEGEND_MAX_ITEMS,
  TREND_CURVE_SIDE_LEGEND_WIDTH_COMPACT,
  TREND_CURVE_SIDE_LEGEND_WIDTH_EXPANDED,
  getTrendCurveLineColor,
} from "../../constants/trendCurveConstants";

export { computeTrendCurveYAxisMax } from "./trendCurveChartHelpers";

type TrendCurveChartRenderOptions = {
  svgElement: SVGSVGElement;
  trendCurveData: TrendCurveChartData;
  width: number;
  height?: number;
  expanded?: boolean;
  interactive?: boolean;
  showContributionLabels?: boolean;
  /**
   * SVG legend placement. Interactive UI uses an HTML side box instead
   * (`none`). PDF export uses `right`.
   */
  legendPlacement?: "none" | "right";
  tooltipElement?: HTMLDivElement | null;
  containerElement?: HTMLDivElement | null;
};

const drawSvgSideLegend = ({
  svg,
  datasets,
  chartHeight,
  legendWidth,
  legendX,
  expanded,
}: {
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  datasets: TrendCurveChartData["datasets"];
  chartHeight: number;
  legendWidth: number;
  legendX: number;
  expanded: boolean;
}) => {
  const items = getTrendCurveLegendItems(
    datasets,
    TREND_CURVE_LEGEND_MAX_ITEMS
  );
  if (!items.length) return;

  const pad = expanded ? 10 : 6;
  const rowH = expanded ? 20 : 16;
  const titleH = expanded ? 18 : 14;
  const fontSize = expanded ? "11px" : "9px";
  const titleSize = expanded ? "11px" : "9px";
  const maxChars = expanded ? 28 : 18;
  const boxHeight = Math.min(
    chartHeight,
    pad * 2 + titleH + items.length * rowH
  );

  const legend = svg
    .append("g")
    .attr("class", "trend-curve-legend")
    .attr("transform", `translate(${legendX},8)`);

  legend
    .append("rect")
    .attr("width", legendWidth)
    .attr("height", boxHeight)
    .attr("rx", 6)
    .attr("ry", 6)
    .attr("fill", "#f9fafb")
    .attr("stroke", "#d1d5db")
    .attr("stroke-width", 1);

  legend
    .append("text")
    .attr("x", pad)
    .attr("y", pad + 10)
    .style("font-family", "Poppins, sans-serif")
    .style("font-size", titleSize)
    .style("font-weight", "600")
    .style("fill", "#374151")
    .text(items.length === 1 ? "Legend" : `Top ${items.length} trends`);

  items.forEach((dataset, index) => {
    const y = pad + titleH + index * rowH;
    const tNoPrefix =
      dataset.tNo != null ? `T${dataset.tNo}. ` : `${index + 1}. `;
    const label = truncateTrendCurveLegendLabel(
      `${tNoPrefix}${formatTrendHeaderChips(dataset.label)}`,
      maxChars
    );

    const item = legend.append("g").attr("transform", `translate(${pad},${y})`);

    item
      .append("line")
      .attr("x1", 0)
      .attr("x2", 14)
      .attr("y1", 8)
      .attr("y2", 8)
      .attr("stroke", dataset.borderColor)
      .attr("stroke-width", 2.5)
      .attr("stroke-linecap", "round");

    item
      .append("circle")
      .attr("cx", 7)
      .attr("cy", 8)
      .attr("r", 2.5)
      .attr("fill", dataset.borderColor)
      .attr("stroke", "#ffffff")
      .attr("stroke-width", 1);

    item
      .append("text")
      .attr("x", 20)
      .attr("y", 11)
      .style("font-family", "Poppins, sans-serif")
      .style("font-size", fontSize)
      .style("font-weight", "500")
      .style("fill", "#1f2937")
      .text(label);
  });
};

export const renderTrendCurveChart = ({
  svgElement,
  trendCurveData,
  width,
  height = TREND_CURVE_CHART_HEIGHT,
  expanded = false,
  interactive = true,
  showContributionLabels = false,
  legendPlacement = "none",
  tooltipElement = null,
  containerElement = null,
}: TrendCurveChartRenderOptions) => {
  if (!trendCurveData.labels.length || !trendCurveData.datasets.length) {
    return;
  }

  const sideLegendWidth =
    legendPlacement === "right"
      ? expanded
        ? TREND_CURVE_SIDE_LEGEND_WIDTH_EXPANDED
        : TREND_CURVE_SIDE_LEGEND_WIDTH_COMPACT
      : 0;
  const legendGap = sideLegendWidth > 0 ? 12 : 0;
  const plotWidth = width - sideLegendWidth - legendGap;

  const svg = d3.select(svgElement);
  svg.selectAll("*").remove();
  svg.attr("width", width).attr("height", height);

  const isSingleSeries = trendCurveData.datasets.length === 1;
  const showPointLabels = showContributionLabels && isSingleSeries;

  // Extra left/right when contribution % labels are drawn so first/last % clear axes.
  const margin = {
    top: 16,
    right: showPointLabels ? (expanded ? 40 : 32) : expanded ? 20 : 18,
    bottom: 0,
    left: showPointLabels ? (expanded ? 106 : 98) : expanded ? 76 : 78,
  };
  const chartWidth = Math.max(40, plotWidth - margin.left - margin.right);
  const contributionLabelLayout = showPointLabels
    ? computeTrendCurveContributionLabelLayout(
        trendCurveData.labels.length,
        chartWidth,
        expanded
      )
    : null;
  if (contributionLabelLayout) {
    margin.top = contributionLabelLayout.topMargin;
  }
  const rotateXLabels = shouldRotateTrendCurveXLabels(
    trendCurveData.labels,
    chartWidth
  );
  margin.bottom = computeTrendCurveBottomMargin(
    trendCurveData.labels,
    chartWidth
  );
  const chartHeight = Math.max(40, height - margin.top - margin.bottom);

  const allValues = trendCurveData.datasets.flatMap(dataset => dataset.data);
  const yAxisMax = computeTrendCurveYAxisMax(allValues);

  const xPadding =
    computeTrendCurveXPadding(trendCurveData.labels.length) +
    (showPointLabels && trendCurveData.labels.length <= 4 ? 0.1 : 0);

  const x = d3
    .scalePoint()
    .domain(trendCurveData.labels)
    .range([0, chartWidth])
    .padding(xPadding);

  const y = d3.scaleLinear().domain([0, yAxisMax]).range([chartHeight, 0]);

  const line = d3
    .line<number>()
    .x((_, i) => x(trendCurveData.labels[i]) ?? 0)
    .y(d => y(d))
    .curve(d3.curveLinear);

  const g = svg
    .append("g")
    .attr("transform", `translate(${margin.left},${margin.top})`);

  const axisFontSize = expanded ? "13px" : "12px";
  const xTickLabels = getTrendCurveXAxisTickLabels(
    trendCurveData.labels,
    chartWidth
  );

  g.append("g")
    .attr("class", "grid")
    .call(
      d3
        .axisLeft(y)
        .tickSize(-chartWidth)
        .tickFormat(() => "")
    )
    .call(group => group.select(".domain").remove())
    .call(group =>
      group
        .selectAll(".tick line")
        .attr("stroke", "#e5e7eb")
        .attr("stroke-dasharray", "2,2")
    );

  g.append("g")
    .call(
      d3
        .axisLeft(y)
        .ticks(5)
        .tickFormat(d => `${d}%`)
    )
    .call(group => group.select(".domain").attr("stroke", "#000000"))
    .selectAll("text")
    .style("font-family", "Poppins, sans-serif")
    .style("font-size", axisFontSize)
    .style("font-weight", "normal")
    .style("fill", "#666666");

  g.append("g")
    .attr("transform", `translate(0,${chartHeight})`)
    .call(d3.axisBottom(x).tickSizeOuter(0).tickValues(xTickLabels))
    .call(group => group.select(".domain").attr("stroke", "#000000"))
    .selectAll("text")
    .style("font-family", "Poppins, sans-serif")
    .style("font-size", axisFontSize)
    .style("font-weight", "normal")
    .style("fill", "#666666")
    .attr("transform", rotateXLabels ? "rotate(-40)" : null)
    .attr("dx", rotateXLabels ? "-0.4em" : "0")
    .attr("dy", rotateXLabels ? "0.15em" : "0.85em")
    .style("text-anchor", rotateXLabels ? "end" : "middle");

  // Y-axis label (rotated), in the dedicated left band.
  g.append("text")
    .attr("transform", "rotate(-90)")
    .attr("x", margin.top - height / 2)
    .attr("y", -margin.left + 20)
    .attr("dy", ".32em")
    .attr("text-anchor", "middle")
    .style("font-family", "Poppins, sans-serif")
    .style("font-size", axisFontSize)
    .style("font-weight", "bold")
    .style("fill", "#666666")
    .text("Contribution");

  // X-axis label, centered below the tick labels.
  // Position within the bottom margin area, below rotated tick labels.
  g.append("text")
    .attr("x", chartWidth / 2)
    .attr("y", chartHeight + margin.bottom - 16)
    .attr("text-anchor", "middle")
    .style("font-family", "Poppins, sans-serif")
    .style("font-size", axisFontSize)
    .style("font-weight", "bold")
    .style("fill", "#666666")
    .text("Time Period");

  const linesGroup = g.append("g");
  const labelsGroup = g.append("g").attr("class", "trend-curve-value-labels");
  const tooltip =
    interactive && tooltipElement ? d3.select(tooltipElement) : null;
  const container = containerElement;

  const positionTooltip = (event: MouseEvent, pointIndex: number) => {
    if (!tooltip || !container || !tooltipElement) return;

    const label = trendCurveData.labels[pointIndex];
    const xPos = x(label) ?? 0;
    const containerRect = container.getBoundingClientRect();
    const tooltipWidth = tooltipElement.offsetWidth || 180;
    const tooltipHeight = tooltipElement.offsetHeight || 90;

    let left = containerRect.left + margin.left + xPos + 14;
    let top = event.clientY - tooltipHeight / 2;

    if (left + tooltipWidth > containerRect.right - 8) {
      left = containerRect.left + margin.left + xPos - tooltipWidth - 14;
    }
    if (top < containerRect.top + 8) {
      top = containerRect.top + 8;
    }
    if (top + tooltipHeight > containerRect.bottom - 8) {
      top = containerRect.bottom - tooltipHeight - 8;
    }

    tooltip
      .style("left", `${left - containerRect.left}px`)
      .style("top", `${top - containerRect.top}px`);
  };

  const showTooltip = (
    event: MouseEvent,
    datasetIndex: number,
    pointIndex: number
  ) => {
    if (!tooltip) return;

    const dataset = trendCurveData.datasets[datasetIndex];
    const period = trendCurveData.labels[pointIndex];
    const contribution = dataset.data[pointIndex];

    tooltip.style("visibility", "visible").html(
      buildTrendCurveTooltipHtml({
        trendName: dataset.label,
        tNo: dataset.tNo,
        period,
        contribution,
        color: dataset.borderColor,
        expanded,
      })
    );

    positionTooltip(event, pointIndex);
  };

  const hideTooltip = () => {
    tooltip?.style("visibility", "hidden");
  };

  const highlightLine = (datasetIndex: number) => {
    linesGroup.selectAll("path.trend-curve-line").style("opacity", 0.12);
    linesGroup
      .selectAll(`path.trend-curve-line-${datasetIndex}`)
      .style("opacity", 1);
    g.selectAll("circle.trend-curve-point").style("opacity", 0.12);
    g.selectAll(`circle.trend-curve-point-${datasetIndex}`).style("opacity", 1);
  };

  const resetHighlight = () => {
    linesGroup
      .selectAll("path.trend-curve-line")
      .style("opacity", isSingleSeries ? 1 : 0.7);
    g.selectAll("circle.trend-curve-point").style("opacity", 0.9);
  };

  const labelFontSize = contributionLabelLayout
    ? `${contributionLabelLayout.fontSizePx}px`
    : expanded
      ? "12px"
      : "11px";
  const labelOffsetY = contributionLabelLayout?.offsetY ?? 11;
  const labelStep = contributionLabelLayout?.labelStep ?? 1;
  const strokeWidth = isSingleSeries
    ? expanded
      ? 4
      : 3.75
    : expanded
      ? 2.5
      : 2.25;
  const pointRadius = isSingleSeries ? (expanded ? 6 : 6) : expanded ? 5 : 4.75;
  const lineOpacity = isSingleSeries ? 1 : 0.7;

  trendCurveData.datasets.forEach((dataset, datasetIndex) => {
    linesGroup
      .append("path")
      .datum(dataset.data)
      .attr("fill", "none")
      .attr("stroke", dataset.borderColor)
      .attr("stroke-width", strokeWidth)
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .attr("d", line)
      .attr("class", `trend-curve-line trend-curve-line-${datasetIndex}`)
      .style("opacity", lineOpacity)
      .style("pointer-events", "none");

    dataset.data.forEach((value, pointIndex) => {
      const cx = x(trendCurveData.labels[pointIndex]) ?? 0;
      const cy = y(value);

      const point = g
        .append("circle")
        .attr("cx", cx)
        .attr("cy", cy)
        .attr("r", pointRadius)
        .attr("fill", dataset.borderColor)
        .attr("stroke", "#ffffff")
        .attr("stroke-width", 2)
        .attr("class", `trend-curve-point trend-curve-point-${datasetIndex}`)
        .style("opacity", 0.9);

      if (
        showPointLabels &&
        shouldShowTrendCurveContributionLabel(
          pointIndex,
          trendCurveData.labels.length,
          labelStep
        )
      ) {
        labelsGroup
          .append("text")
          .attr("x", cx)
          .attr("y", cy - labelOffsetY)
          .attr("text-anchor", "middle")
          .attr("class", "trend-curve-point-label")
          .style("font-family", "Poppins, sans-serif")
          .style("font-size", labelFontSize)
          .style("font-weight", isSingleSeries ? "700" : "600")
          .style("fill", dataset.borderColor)
          .style("pointer-events", "none")
          .text(formatTrendCurveContribution(value));
      }

      if (interactive) {
        point
          .style("cursor", "pointer")
          .on("mouseover", event => {
            highlightLine(datasetIndex);
            showTooltip(event, datasetIndex, pointIndex);
          })
          .on("mousemove", event => {
            showTooltip(event, datasetIndex, pointIndex);
          })
          .on("mouseout", () => {
            hideTooltip();
            resetHighlight();
          });
      }
    });
  });

  if (legendPlacement === "right" && sideLegendWidth > 0) {
    drawSvgSideLegend({
      svg,
      datasets: trendCurveData.datasets,
      chartHeight: height - 16,
      legendWidth: sideLegendWidth,
      legendX: plotWidth + legendGap,
      expanded,
    });
  }
};

export const setTrendCurveDatasetHighlight = (
  svgElement: SVGSVGElement | null,
  datasetIndex: number | null
) => {
  if (!svgElement) return;

  const svg = d3.select(svgElement);
  const lines = svg.selectAll("path.trend-curve-line");
  const points = svg.selectAll("circle.trend-curve-point");
  const isSingleSeries = lines.size() === 1;

  if (datasetIndex === null) {
    lines.style("opacity", isSingleSeries ? 1 : 0.7);
    points.style("opacity", 0.9);
    return;
  }

  lines.style("opacity", 0.12);
  svg.selectAll(`path.trend-curve-line-${datasetIndex}`).style("opacity", 1);
  points.style("opacity", 0.12);
  svg.selectAll(`circle.trend-curve-point-${datasetIndex}`).style("opacity", 1);
};

export const createTrendCurveSvgMarkup = (
  trendCurveData: TrendCurveChartData,
  /** Max plot width (px); actual width shrinks when few periods. */
  width: number,
  options?: { expanded?: boolean; height?: number; showLegend?: boolean }
) => {
  if (typeof document === "undefined") {
    return "";
  }

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("id", "trend-curves-chart");
  svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");

  const expanded =
    options?.expanded ?? width >= TREND_CURVE_EXPANDED_CHART_WIDTH;
  const height =
    options?.height ??
    (expanded ? TREND_CURVE_EXPANDED_CHART_HEIGHT : TREND_CURVE_CHART_HEIGHT);
  const showLegend = options?.showLegend ?? true;
  const isSingleSeries = trendCurveData.datasets.length === 1;
  const legendWidth = showLegend
    ? expanded
      ? TREND_CURVE_SIDE_LEGEND_WIDTH_EXPANDED
      : TREND_CURVE_SIDE_LEGEND_WIDTH_COMPACT
    : 0;
  const preferredPlotWidth = computeTrendCurvePreferredChartWidth(
    trendCurveData.labels.length,
    expanded,
    width
  );
  const plotWidth = preferredPlotWidth;
  const totalWidth = plotWidth + (showLegend ? legendWidth + 12 : 0);

  renderTrendCurveChart({
    svgElement: svg,
    trendCurveData,
    width: totalWidth,
    height,
    expanded,
    interactive: false,
    showContributionLabels: isSingleSeries,
    legendPlacement: showLegend ? "right" : "none",
  });

  const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
  style.textContent = `* { font-family: 'Poppins', sans-serif; }`;
  svg.insertBefore(style, svg.firstChild);

  return new XMLSerializer().serializeToString(svg);
};
